import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import Modal from './Modal';
import useTranslations from '../../hooks/useTranslation';
import { updateDiscipleApi } from '../../api';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/authStore';

const AcceptMoveModal = () => {
  const { translate } = useTranslations();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, selectedOrganization, isSuperAdminMasquerading } = useAuthStore(state => state);
  const isMasquerading = Boolean(isSuperAdminMasquerading) || Boolean(user?.is_impersonate) || Boolean(user?.isSuperAdminMasquerading) || Boolean(useAuthStore.getState().isSuperAdminMasquerading);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const pendingInvite = user?.pending_move_invite;
  const action = searchParams.get('action') || pendingInvite?.action;
  const userId = searchParams.get('userId') || searchParams.get('discipleId') || pendingInvite?.userId;
  const groupId = searchParams.get('groupId') || pendingInvite?.groupId;
  const orgId = searchParams.get('orgId') || pendingInvite?.orgId;
  const groupName = searchParams.get('groupName') || pendingInvite?.groupName || '';
  const orgName = searchParams.get('orgName') || pendingInvite?.orgName || '';
  const priorOrgName = searchParams.get('priorOrgName') || pendingInvite?.priorOrgName || (selectedOrganization?.id && String(selectedOrganization.id) !== String(orgId) ? selectedOrganization?.name : '');
  const priorGroupName = searchParams.get('priorGroupName') || pendingInvite?.priorGroupName || '';

  useEffect(() => {
    if (isMasquerading) {
      setOpen(false);
      return;
    }
    if (action === 'move_disciple' && userId && user?.id) {
      const targetUserId = String(userId).replace('user_', '');
      if (String(user.id) !== targetUserId || (!user?.pending_move_invite && searchParams.get('action'))) {
        toast.error(translate('This invitation link has expired or been removed.'));
        handleClose();
        navigate('/');
      } else {
        setOpen(true);
      }
    } else if (action === 'move_disciple' && userId && !user?.id) {
      if (window.location.pathname !== '/login') {
        navigate(`/login${window.location.search}`);
      }
    }
  }, [action, userId, user?.id, user?.pending_move_invite, isMasquerading]);

  const handleClose = () => {
    setOpen(false);
    searchParams.delete('action');
    searchParams.delete('userId');
    searchParams.delete('groupId');
    searchParams.delete('orgId');
    searchParams.delete('inviterId');
    searchParams.delete('groupName');
    searchParams.delete('orgName');
    searchParams.delete('priorOrgName');
    searchParams.delete('priorGroupName');
    setSearchParams(searchParams);
  };

  const handleAccept = async () => {
    if (!user?.id) {
      toast.error(translate('Please login to your account first to accept this invitation.'));
      setOpen(false);
      navigate(`/login${window.location.search}`);
      return;
    }
    try {
      setLoading(true);
      const targetId = String(userId).startsWith('user_') ? userId : `user_${userId}`;
      const res = await updateDiscipleApi(targetId, {
        group_id: groupId || null,
        organization_id: orgId || null,
        is_accept: true,
        is_notify: false
      }, { headers: { 'x-suppress-toast': 'true' } });
      setLoading(false);
      if (res?.data?.status === 200) {
        // toast.success(translate('Invitation accepted successfully! Welcome to your new organization/group.'));
        if (user?.id && orgId) {
          const targetOrg = user?.organizations?.find(o => String(o.id) === String(orgId)) || {
            id: Number(orgId),
            name: orgName ? decodeURIComponent(orgName) : ''
          };
          const currentUserOrgs = user?.user_organizations ? [...user.user_organizations] : [];
          const existingIndex = currentUserOrgs.findIndex(uo => String(uo.organization_id) === String(orgId) && uo.user_role === 'DISCIPLE');
          if (existingIndex !== -1) {
            currentUserOrgs[existingIndex] = { ...currentUserOrgs[existingIndex], status: 'ACTIVE' };
          } else {
            currentUserOrgs.push({
              organization_id: Number(orgId),
              user_role: 'DISCIPLE',
              status: 'ACTIVE',
              approved_level: 'D1,D2,D3'
            });
          }
          useAuthStore.getState().updateUserData({
            user_role: 'DISCIPLE',
            user_organizations: currentUserOrgs,
            pending_move_invite: null
          });
          useAuthStore.getState().setOrganization(targetOrg);
        }
        handleClose();
        if (user?.id) {
          navigate('/disciple');
        } else {
          navigate(`/login?targetOrgId=${orgId || ''}`);
        }
      } else {
        const errorMsg = res?.response?.data?.message || res?.data?.message || translate('Failed to accept invitation.');
        toast.error(errorMsg);
      }
    } catch (error) {
      setLoading(false);
      toast.error(translate('An error occurred while accepting the invitation.'));
    }
  };

  const handleReject = async () => {
    try {
      if (userId && (user?.id || searchParams.get('userId'))) {
        setLoading(true);
        const targetId = String(userId).startsWith('user_') ? userId : `user_${userId}`;
        await updateDiscipleApi(targetId, {
          group_id: groupId || null,
          organization_id: orgId || null,
          is_decline: true,
          is_notify: false
        }, { headers: { 'x-suppress-toast': 'true' } });
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
    toast.success(translate('Invitation declined.'));
    handleClose();
    if (!user?.id) {
      navigate('/login');
    } else {
      useAuthStore.getState().updateUserData({
        pending_move_invite: null
      });
      navigate('/disciple');
    }
  };

  const targetOrgName = orgName ? decodeURIComponent(orgName) : translate('the new organization');
  const currentOrgName = priorOrgName ? decodeURIComponent(priorOrgName) : (selectedOrganization?.name || translate('your current organization'));

  const renderWithOrgName = (templateKey, orgNameVal) => {
    const translatedText = translate(templateKey);
    if (!translatedText.includes('{orgName}')) {
      return translatedText;
    }
    const parts = translatedText.split('{orgName}');
    return (
      <>
        {parts[0]}
        <strong>{orgNameVal}</strong>
        {parts[1]}
      </>
    );
  };

  if (!open || isMasquerading) return null;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={handleReject}
      title={renderWithOrgName('Invitation to Join {orgName}', targetOrgName)}
      modalbgColor="#ffffff"
      modalTextColor="#000000"
      modalTitleColor="#d32f2f"
      disableBackdropClose={loading}
      disableEscapeKeyDown={loading}
      backdropBlur
      buttons={[
        {
          name: translate('Decline'),
          onClick: handleReject,
          buttonBgColor: '#ffffff',
          buttonTextColor: 'black',
          buttonHoverColor: '#f5f5f5',
          type: 'outlined',
          disabled: loading,
          height: '40px'
        },
        {
          name: translate('Accept'),
          onClick: handleAccept,
          buttonBgColor: '#d32f2f',
          buttonTextColor: '#ffffff',
          buttonHoverColor: '#b71c1c',
          disabled: loading,
          loading: loading,
          height: '40px'
        }
      ]}
    >
      <Box sx={{ p: 1, textAlign: 'left' }}>
        <Typography variant="body1" sx={{ color: '#000000', mb: 1, lineHeight: 1.4 }}>
          {renderWithOrgName('You have been invited by {orgName} to continue your discipleship journey with their organization.', targetOrgName)}
        </Typography>

        <Typography variant="body1" sx={{ color: '#000000', mb: 1, lineHeight: 1.4 }}>
          {renderWithOrgName('You are currently a member of {orgName}.', currentOrgName)}
        </Typography>

        <Typography variant="body1" sx={{ color: '#000000', mb: 1, fontWeight: 600 }}>
          {translate('If you accept:')}
        </Typography>

        <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1, '& li': { mb: 0.5, color: '#000000', fontSize: '14.5px', lineHeight: 1.4 } }}>
          <li>
            {renderWithOrgName('Your active membership will move to {orgName}.', targetOrgName)}
          </li>
          <li>
            {translate('Your previous participation and completed years will remain part of your history.')}
          </li>
          <li>
            {translate('You can only be in one active organization at a time.')}
          </li>
        </Box>

        <Typography variant="body1" sx={{ color: '#000000', fontStyle: 'italic', mt: 2, mb:-2 }}>
          {translate('Do you want to accept this invitation?')}
        </Typography>
      </Box>
    </Modal>
  );
};

export default AcceptMoveModal;
